import { FormEvent, useEffect, useState } from "react";
import { api } from "../api/client";
import type { PIDDevice, PIDDeviceStatus, Vehicle, ListResponse, MACChangeRequest } from "../api/types";

const STATUS_TABS: { value: PIDDeviceStatus | ""; label: string }[] = [
  { value: "", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "active", label: "Active" },
  { value: "disabled", label: "Disabled" },
  { value: "reregistration_requested", label: "Re-register" },
];

function statusPill(s: PIDDeviceStatus) {
  const tone =
    s === "active" ? "ok" : s === "pending" ? "warn" : s === "disabled" ? "muted" : "info";
  const label =
    s === "reregistration_requested" ? "RE-REGISTER" : s.toUpperCase();
  return <span className={`users-status-pill ${tone}`}>{label}</span>;
}

function ago(iso?: string): string {
  if (!iso) return "never";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "never";
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

function unwrapVehicles(res: ListResponse<Vehicle> | Vehicle[]): Vehicle[] {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res.data)) return res.data;
  return [];
}

export default function PIDDevicesPage() {
  const [devices, setDevices] = useState<PIDDevice[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [statusFilter, setStatusFilter] = useState<PIDDeviceStatus | "">("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [pending, setPending] = useState(false);
  const [hwId, setHwId] = useState("");
  const [mac, setMac] = useState("");
  const [model, setModel] = useState("");
  const [label, setLabel] = useState("");

  const [editId, setEditId] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState("");
  const [editMac, setEditMac] = useState("");
  const [editModel, setEditModel] = useState("");
  const [editVehicle, setEditVehicle] = useState("");
  const [editPending, setEditPending] = useState(false);

  const [actionPending, setActionPending] = useState<string | null>(null);

  // Setup PIN state
  const [setupPin, setSetupPin] = useState("");
  const [pinStatus, setPinStatus] = useState<"unknown" | "set" | "not_set">("unknown");
  const [pinPending, setPinPending] = useState(false);

  // MAC change requests state
  const [macRequests, setMacRequests] = useState<MACChangeRequest[]>([]);
  const [macRequestCount, setMacRequestCount] = useState(0);
  const [rejectNoteId, setRejectNoteId] = useState<string | null>(null);
  const [rejectNote, setRejectNote] = useState("");

  async function loadPinStatus() {
    try {
      const settings = await api.getSettings();
      const val = settings?.["pid.setup_pin_hash"];
      setPinStatus(val && val !== "" ? "set" : "not_set");
    } catch { setPinStatus("unknown"); }
  }

  async function onRotatePin(e: FormEvent) {
    e.preventDefault();
    if (!setupPin.trim()) return;
    setPinPending(true);
    setError(null);
    try {
      await api.rotatePIDSetupPin(setupPin.trim());
      setSuccess("PID setup PIN updated");
      setSetupPin("");
      setPinStatus("set");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to set PIN");
    } finally { setPinPending(false); }
  }

  async function loadMacRequests() {
    try {
      const [res, countRes] = await Promise.all([
        api.listMACChangeRequests("pending"),
        api.countPendingMACChanges(),
      ]);
      setMacRequests(res.data ?? []);
      setMacRequestCount(countRes.count);
    } catch { /* ignore */ }
  }

  async function onApproveMac(id: string) {
    setError(null);
    setActionPending(id);
    try {
      await api.approveMACChange(id);
      setSuccess("MAC change approved");
      await Promise.all([load(), loadMacRequests()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to approve");
    } finally { setActionPending(null); }
  }

  async function onRejectMac(id: string) {
    setError(null);
    setActionPending(id);
    try {
      await api.rejectMACChange(id, rejectNote);
      setSuccess("MAC change rejected");
      setRejectNoteId(null);
      setRejectNote("");
      await loadMacRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reject");
    } finally { setActionPending(null); }
  }

  async function load() {
    setLoading(true);
    try {
      const [devRes, vehRes] = await Promise.all([
        api.listPIDDevices(statusFilter || undefined),
        api.listVehicles(),
      ]);
      setDevices(devRes.data ?? []);
      setVehicles(unwrapVehicles(vehRes));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load PID devices");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); void loadPinStatus(); void loadMacRequests(); }, [statusFilter]);

  async function onCreate(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setPending(true);
    try {
      const created = await api.createPIDDevice({
        hardware_id: hwId.trim().toLowerCase(),
        mac_address: mac.trim().toUpperCase() || undefined,
        device_model: model.trim() || undefined,
        device_label: label.trim() || undefined,
      });
      setSuccess(`Pre-registered device ${created.hardware_id}`);
      setHwId("");
      setMac("");
      setModel("");
      setLabel("");
      setShowCreate(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create device");
    } finally {
      setPending(false);
    }
  }

  function startEdit(d: PIDDevice) {
    setEditId(d.id);
    setEditLabel(d.device_label ?? "");
    setEditMac(d.mac_address ?? "");
    setEditModel(d.device_model ?? "");
    setEditVehicle(d.vehicle_id ?? "");
  }

  async function onUpdate(e: FormEvent) {
    e.preventDefault();
    if (!editId) return;
    setError(null);
    setSuccess(null);
    setEditPending(true);
    try {
      await api.updatePIDDevice(editId, {
        device_label: editLabel.trim(),
        mac_address: editMac.trim().toUpperCase(),
        device_model: editModel.trim(),
        vehicle_id: editVehicle || undefined,
      });
      setSuccess("Device updated");
      setEditId(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update device");
    } finally {
      setEditPending(false);
    }
  }

  async function onActivate(id: string) {
    setError(null);
    setSuccess(null);
    setActionPending(id);
    try {
      await api.activatePIDDevice(id);
      setSuccess("Device activated");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to activate");
    } finally {
      setActionPending(null);
    }
  }

  async function onDisable(id: string) {
    setError(null);
    setSuccess(null);
    setActionPending(id);
    try {
      await api.disablePIDDevice(id);
      setSuccess("Device disabled");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to disable");
    } finally {
      setActionPending(null);
    }
  }

  async function onDelete(d: PIDDevice) {
    if (!confirm(`Delete PID device ${d.hardware_id}? This cannot be undone.`)) return;
    setError(null);
    setSuccess(null);
    setActionPending(d.id);
    try {
      await api.deletePIDDevice(d.id);
      setSuccess(`Deleted ${d.hardware_id}`);
      if (editId === d.id) setEditId(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete");
    } finally {
      setActionPending(null);
    }
  }

  return (
    <div className="page users-page">
      <div className="users-header">
        <div>
          <h1>PID Devices</h1>
          <p className="secondary-text">Passenger Information Display tablet management.</p>
        </div>
        <button
          className="btn primary users-add-btn"
          type="button"
          onClick={() => setShowCreate((v) => !v)}
        >
          {showCreate ? "Cancel" : "+ Pre-register device"}
        </button>
      </div>

      {success && <div className="banner banner-success">{success}</div>}
      {error && <div className="banner banner-error">{error}</div>}

      {/* Setup PIN card */}
      <section className="card" style={{ marginBottom: 16 }}>
        <h3>PID Setup PIN {pinStatus === "set" ? <span className="users-status-pill ok">SET</span> : pinStatus === "not_set" ? <span className="users-status-pill warn">NOT SET</span> : null}</h3>
        <p className="secondary-text" style={{ margin: "4px 0 8px" }}>Required for on-device MAC address entry. Shared per agency.</p>
        <form style={{ display: "flex", gap: 8, alignItems: "flex-end" }} onSubmit={onRotatePin}>
          <label>
            New PIN (4-16 chars)
            <input
              type="password"
              value={setupPin}
              onChange={(e) => setSetupPin(e.target.value)}
              minLength={4}
              maxLength={16}
              required
              style={{ width: 180 }}
            />
          </label>
          <button className="btn primary" type="submit" disabled={pinPending || setupPin.trim().length < 4}>
            {pinPending ? "Saving…" : pinStatus === "set" ? "Rotate PIN" : "Set PIN"}
          </button>
        </form>
      </section>

      {showCreate && (
        <section className="card">
          <h3>Pre-register PID device</h3>
          <form className="form-grid" onSubmit={onCreate}>
            <div className="form-row">
              <label>
                Hardware ID (ANDROID_ID)
                <input
                  value={hwId}
                  onChange={(e) => setHwId(e.target.value)}
                  placeholder="e.g. a1b2c3d4e5f6"
                  pattern="[a-fA-F0-9]{1,128}"
                  required
                />
              </label>
              <label>
                MAC address (optional)
                <input
                  value={mac}
                  onChange={(e) => setMac(e.target.value)}
                  placeholder="AA:BB:CC:DD:EE:FF"
                />
              </label>
            </div>
            <div className="form-row">
              <label>
                Device model (optional)
                <input value={model} onChange={(e) => setModel(e.target.value)} placeholder="e.g. Samsung Tab A8" />
              </label>
              <label>
                Label (optional)
                <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Bus 42 rear screen" />
              </label>
            </div>
            <button className="btn primary" type="submit" disabled={pending}>
              {pending ? "Creating…" : "Pre-register"}
            </button>
          </form>
        </section>
      )}

      {editId && (
        <section className="card">
          <h3>Edit device</h3>
          <form className="form-grid" onSubmit={onUpdate}>
            <div className="form-row">
              <label>
                Label
                <input value={editLabel} onChange={(e) => setEditLabel(e.target.value)} />
              </label>
              <label>
                MAC address
                <input value={editMac} onChange={(e) => setEditMac(e.target.value)} placeholder="AA:BB:CC:DD:EE:FF" />
              </label>
              <label>
                Device model
                <input value={editModel} onChange={(e) => setEditModel(e.target.value)} />
              </label>
            </div>
            <div className="form-row">
              <label>
                Assigned vehicle
                <select value={editVehicle} onChange={(e) => setEditVehicle(e.target.value)}>
                  <option value="">— None —</option>
                  {vehicles.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.vehicle_id} {v.vehicle_label ? `(${v.vehicle_label})` : ""}
                    </option>
                  ))}
                </select>
              </label>
              <button className="btn ghost" type="button" onClick={() => setEditId(null)} disabled={editPending}>Cancel</button>
              <button className="btn primary" type="submit" disabled={editPending}>
                {editPending ? "Saving…" : "Save"}
              </button>
            </div>
          </form>
        </section>
      )}

      <section className="card users-list-card">
        <div className="users-list-head">
          <h3>
            <span className="users-bar" />
            Devices
            <span className="users-count-pill">{devices.length}</span>
          </h3>
          <div style={{ display: "flex", gap: 4 }}>
            {STATUS_TABS.map((t) => (
              <button
                key={t.value}
                type="button"
                className={"btn ghost" + (statusFilter === t.value ? " active" : "")}
                onClick={() => setStatusFilter(t.value)}
                style={statusFilter === t.value ? { fontWeight: 600, textDecoration: "underline" } : undefined}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="users-table-wrap">
          <table className="users-table">
            <thead>
              <tr>
                <th>Hardware ID</th>
                <th>Label</th>
                <th>Vehicle</th>
                <th>Status</th>
                <th>Last seen</th>
                <th>Version</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && devices.length === 0 && (
                <tr><td colSpan={7} className="empty">Loading…</td></tr>
              )}
              {!loading && devices.length === 0 && (
                <tr><td colSpan={7} className="empty">— no devices —</td></tr>
              )}
              {devices.map((d) => (
                <tr key={d.id} className={"users-row" + (editId === d.id ? " users-row-selected" : "")}>
                  <td className="mono">{d.hardware_id}</td>
                  <td>{d.device_label || <span className="secondary-text">—</span>}</td>
                  <td>
                    {d.vehicle_code
                      ? `${d.vehicle_code}${d.vehicle_label ? ` (${d.vehicle_label})` : ""}`
                      : <span className="secondary-text">unassigned</span>}
                  </td>
                  <td>{statusPill(d.status)}</td>
                  <td className="secondary-text">{ago(d.last_seen_at)}</td>
                  <td className="secondary-text">{d.app_version || "—"}</td>
                  <td>
                    <div className="users-row-actions">
                      <button className="btn ghost users-edit-btn" type="button" onClick={() => startEdit(d)}>Edit</button>
                      {d.status === "pending" && (
                        <button
                          className="btn ghost"
                          type="button"
                          disabled={actionPending === d.id}
                          onClick={() => void onActivate(d.id)}
                        >
                          Activate
                        </button>
                      )}
                      {d.status === "active" && (
                        <button
                          className="btn ghost"
                          type="button"
                          disabled={actionPending === d.id}
                          onClick={() => void onDisable(d.id)}
                        >
                          Disable
                        </button>
                      )}
                      {d.status === "disabled" && (
                        <button
                          className="btn ghost"
                          type="button"
                          disabled={actionPending === d.id}
                          onClick={() => void onActivate(d.id)}
                        >
                          Re-activate
                        </button>
                      )}
                      <button
                        className="btn ghost users-delete-btn"
                        type="button"
                        disabled={actionPending === d.id}
                        onClick={() => void onDelete(d)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* MAC Change Requests */}
      {macRequestCount > 0 && (
        <section className="card" style={{ marginTop: 16 }}>
          <h3>
            <span className="users-bar" />
            MAC Change Requests
            <span className="users-count-pill" style={{ background: "#E8620A", color: "#fff" }}>{macRequestCount}</span>
          </h3>
          <div className="users-table-wrap">
            <table className="users-table">
              <thead>
                <tr>
                  <th>Hardware ID</th>
                  <th>Old MAC</th>
                  <th>New MAC</th>
                  <th>Requested</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {macRequests.map((req) => (
                  <tr key={req.id} className="users-row">
                    <td className="mono">{req.hardware_id}</td>
                    <td className="mono">{req.old_mac || "—"}</td>
                    <td className="mono">{req.new_mac}</td>
                    <td className="secondary-text">{ago(req.requested_at)}</td>
                    <td>
                      <div className="users-row-actions">
                        {rejectNoteId === req.id ? (
                          <>
                            <input
                              value={rejectNote}
                              onChange={(e) => setRejectNote(e.target.value)}
                              placeholder="Reason (optional)"
                              style={{ width: 140 }}
                            />
                            <button className="btn ghost" type="button" disabled={actionPending === req.id} onClick={() => void onRejectMac(req.id)}>Confirm Reject</button>
                            <button className="btn ghost" type="button" onClick={() => { setRejectNoteId(null); setRejectNote(""); }}>Cancel</button>
                          </>
                        ) : (
                          <>
                            <button className="btn ghost" style={{ color: "#16a34a" }} type="button" disabled={actionPending === req.id} onClick={() => void onApproveMac(req.id)}>Approve</button>
                            <button className="btn ghost users-delete-btn" type="button" disabled={actionPending === req.id} onClick={() => setRejectNoteId(req.id)}>Reject</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
